import { useCallback, useSyncExternalStore } from "react";

import { GUIDE_STEPS, type GuideStep } from "../../content/guided-help";

const eventName = "kv-guide-reading-change";
const volatile = new Map<string, GuideStep>();
function read(key: string): GuideStep {
  try {
    const value = sessionStorage.getItem(key);
    return GUIDE_STEPS.includes(value as GuideStep) ? (value as GuideStep) : "client";
  } catch {
    return volatile.get(key) ?? "client";
  }
}
/** Same-context readers share a position; a remote reader never takes keyboard focus. */
export function useGuideReading(key: string) {
  const subscribe = useCallback(
    (notify: () => void) => {
      const local = (event: Event) => {
        if ((event as CustomEvent<string>).detail === key) notify();
      };
      const storage = (event: StorageEvent) => {
        if (event.storageArea === sessionStorage && (event.key === key || event.key === null))
          notify();
      };
      window.addEventListener(eventName, local);
      window.addEventListener("storage", storage);
      return () => {
        window.removeEventListener(eventName, local);
        window.removeEventListener("storage", storage);
      };
    },
    [key],
  );
  const snapshot = useCallback(() => read(key), [key]);
  const step = useSyncExternalStore(subscribe, snapshot, () => "client" as GuideStep);
  const change = (next: GuideStep) => {
    volatile.set(key, next);
    try {
      sessionStorage.setItem(key, next);
    } catch {
      /* Optional persistence. */
    }
    window.dispatchEvent(new CustomEvent(eventName, { detail: key }));
  };
  return [step, change] as const;
}
